import React, { useState, useEffect, useRef } from 'react';
import Card from '../../../components/Global/Card';
import { AiOutlineIdcard, AiOutlineEdit } from 'react-icons/ai';
import DataTable from '../../../components/Global/DataTable';
import Modal from '../../../components/Global/Modal';
import { CustomService } from '../../../services/api/custom/CustomService';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import GoBack from '../../../components/Utils/GoBack';
import { toast } from 'react-toastify';
import OrcamentoTable from './OrcamentoTable'; // Importando o componente OrcamentoTable

const ClientesPage = ({ user }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const navigate = useNavigate();
  const didFetch = useRef(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [orcamentosByCliente, setOrcamentosByCliente] = useState({});

  const handleExpand = (expanded, record) => {
    if (expanded) {
      fetchOrcamentosByCliente(record.id);
      setExpandedRowKeys([record.id]);
    } else {
      setExpandedRowKeys([]);
    }
  };

  const [codigoConvite, setCodigoConvite] = useState(null);
  const [clienteParaCodigo, setClienteParaCodigo] = useState(null);
  const [showCodigoModal, setShowCodigoModal] = useState(false);

  const gerarCodigoConvite = async (cliente) => {
    try {
      const resp = await CustomService.getRequest(
        `/interno/cliente/${cliente.id}/codigo`
      );
      setCodigoConvite(resp || 'Código não encontrado');
      setClienteParaCodigo(cliente);
      setShowCodigoModal(true);
    } catch (error) {
      console.error('Erro ao gerar código de convite:', error);
      toast.error('Erro ao gerar código de convite.');
    }
  };

  const fetchOrcamentosByCliente = async (clienteId) => {
    if (
      orcamentosByCliente[clienteId] &&
      orcamentosByCliente[clienteId] !== 'error'
    ) {
      return; // Já temos no cache, não busca novamente
    }
    try {
      const response = await CustomService.getRequest(
        `/interno/orcamentos?cliente_id=${clienteId}`
      );
      setOrcamentosByCliente((prev) => ({
        ...prev,
        [clienteId]: response,
      }));
    } catch (error) {
      setOrcamentosByCliente((prev) => ({
        ...prev,
        [clienteId]: 'error',
      }));
      console.error('Erro ao buscar orçamentos:', error);
    }
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    fetchData();
  }, []);

  useEffect(() => {
    if (!user.admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let clientes = await CustomService.getRequest('/interno/clientes');
      if (!user.admin) {
        clientes = clientes.filter((c) => !c.admin);
      }
      setData(clientes);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      toast.error('Não foi possível carregar os clientes.');
    } finally {
      setLoading(false);
    }
  };

  const removeCliente = async () => {
    try {
      await CustomService.deleteRequest(`/interno/cliente/${toRemove.id}`);
      toast.success('Cliente excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      toast.error('Erro ao excluir cliente.');
    } finally {
      setToRemove(null);
      setShowRemoveModal(false);
    }
  };

  const columns = [
    { title: 'CNPJ', dataIndex: 'cnpj', key: 'cnpj' },
    { title: 'Razão Social', dataIndex: 'razao_social', key: 'razao_social' },
    {
      title: 'Nome Responsável',
      dataIndex: 'nome_responsavel',
      key: 'nome_responsavel',
    },
    {
      title: 'Telefone',
      dataIndex: 'telefone_contato',
      key: 'telefone_contato',
    },
    {
      title: 'Parceiro',
      dataIndex: 'parceiro',
      key: 'parceiro',
      render: (value) => (
        <div
          className={`px-2 py-[2px] rounded-full text-[12px] text-center font-semibold w-fit
        ${value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
      `}
        >
          {value ? 'Parceiro' : 'Não parceiro'}
        </div>
      ),
    },
    {
      title: 'Convite',
      key: 'convite',
      render: (_, record) => (
        <button
          className="text-purple-600 hover:text-purple-800 underline text-sm"
          onClick={() => gerarCodigoConvite(record)}
        >
          Gerar Código
        </button>
      ),
    },
    {
      title: 'Orçamentos',
      key: 'orcamentos',
      render: (_, record) => (
        <button
          className={`text-sm font-medium underline ${
            expandedRowKeys.includes(record.id)
              ? 'text-blue-700'
              : 'text-blue-500 hover:text-blue-700'
          }`}
          onClick={() => {
            const isExpanded = expandedRowKeys.includes(record.id);
            if (isExpanded) {
              setExpandedRowKeys([]);
            } else {
              fetchOrcamentosByCliente(record.id);
              setExpandedRowKeys([record.id]);
            }
          }}
        >
          {expandedRowKeys.includes(record.id)
            ? 'Ocultar Orçamentos'
            : 'Ver Orçamentos'}
        </button>
      ),
    },
  ];

  const actions = {
    onEdit: (record) => navigate(`/cadastro/cliente/${record.id}`),
    onDelete: (record) => {
      setToRemove(record);
      setShowRemoveModal(true);
    },
  };

  return (
    <div className="flex flex-col w-full gap-3">
      <GoBack path="/dashboard" />

      {showRemoveModal && (
        <Modal
          buttonName="Excluir"
          title="Confirmar exclusão"
          handleModal={setShowRemoveModal}
          eventConfirmation={removeCliente}
          size="md:w-1/3"
        >
          <div className="flex flex-col justify-center items-center">
            <span>
              Deseja excluir o cliente <b>{toRemove.razao_social}</b>?
            </span>
            <span className="text-[14px]">Esta ação é irreversível.</span>
          </div>
        </Modal>
      )}

      {showCodigoModal && (
        <Modal
          buttonName="Fechar"
          title="Link de Convite"
          handleModal={setShowCodigoModal}
          eventConfirmation={() => setShowCodigoModal(false)}
          size="md:w-1/3"
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <p>
              Envie o link abaixo para o cliente{' '}
              <b>{clienteParaCodigo?.razao_social}</b>:
            </p>
            <div className="bg-gray-100 px-4 py-2 rounded text-sm break-all">
              https://foxsuprimentos.com.br/definir-senha/{codigoConvite}
            </div>
            <button
              className="mt-1 text-sm text-blue-600 hover:underline"
              onClick={() => {
                navigator.clipboard.writeText(
                  `https://foxsuprimentos.com.br/definir-senha/${codigoConvite}`
                );
                toast.success('Link copiado para a área de transferência!');
              }}
            >
              Copiar Link
            </button>
          </div>
        </Modal>
      )}

      <Card
        buttonActionPath="/cadastro/cliente"
        title="Clientes"
        icon={<AiOutlineIdcard className="w-[25px] h-[25px]" />}
      >
        <div className="w-full">
          <DataTable
            size="100%"
            data={data}
            columns={columns}
            paginationOptions={{ pageSize: 5000 }}
            actions={actions}
            loading={loading}
            actionIcons={{ edit: <AiOutlineEdit /> }}
            expandable={{
              expandedRowRender: (record) => (
                <OrcamentoTable
                  data={orcamentosByCliente[record.id]}
                  loading={!orcamentosByCliente[record.id]}
                />
              ),
              rowExpandable: () => true,
              expandedRowKeys: expandedRowKeys,
              onExpand: handleExpand,
              expandIconColumnIndex: -1,
              expandIcon: () => null,
            }}
          />
        </div>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps)(ClientesPage);
